import React from 'react';

import { useIntl } from 'react-intl';

import Heading from '@atlaskit/heading';
import SearchIcon from '@atlaskit/icon/core/search';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, Grid, Inline, Stack, Text, xcss } from '@atlaskit/primitives';
import Skeleton from '@atlaskit/skeleton';

import { Link } from '../link-component';

import { messages } from './messages';

type CandidateProp = {
	label: string;
	href: string;
};

type LeapQueryProps = {
	candidates: CandidateProp[];
	loading?: boolean;
	queryHash?: string;
	titleIndex?: number;
	handleCandidateClicked?: (candidateIndex: number) => void;
};

const candidateStyles = xcss({
	height: '100%',
	borderRadius: 'radius.full',
	':hover': {
		backgroundColor: 'color.background.neutral.hovered',
	},
});

const candidateContentStyles = xcss({
	height: '100%',
});

const linkStyles = xcss({
	color: 'color.text',
	textDecoration: 'none',

	':hover': {
		textDecoration: 'none',
		color: 'color.text',
	},

	':visited': {
		textDecoration: 'none',
		color: 'color.text',
	},

	':focus': {
		textDecoration: 'none',
		color: 'color.text',
	},
});

const candidatesGridStyle = xcss({
	gridAutoRows: '1fr',
});

const Candidate = ({
	candidate,
	candidateIndex,
	handleCandidateClicked,
}: {
	candidate: CandidateProp;
	candidateIndex: number;
	handleCandidateClicked?: (candidateIndex: number) => void;
}) => {
	return (
		<Link
			href={candidate.href}
			xcss={linkStyles}
			onClick={() => {
				if (handleCandidateClicked) {
					handleCandidateClicked(candidateIndex);
				}
			}}
		>
			<Box
				paddingBlock="space.150"
				paddingInline="space.300"
				backgroundColor="color.background.neutral"
				xcss={candidateStyles}
			>
				<Inline
					space="space.150"
					alignBlock="center"
					alignInline="start"
					xcss={candidateContentStyles}
				>
					<SearchIcon label={`search ${candidate}`} />
					<Text>{candidate.label}</Text>
				</Inline>
			</Box>
		</Link>
	);
};

const Candidates = ({
	candidates,
	titleIndex,
	handleCandidateClicked,
}: {
	candidates: CandidateProp[];
	titleIndex: number;
	handleCandidateClicked?: (candidateIndex: number) => void;
}) => {
	const intl = useIntl();
	return (
		<Stack space="space.200">
			<Heading size="small">{intl.formatMessage(titleMessagesArray[titleIndex])}</Heading>
			<Grid
				rowGap="space.200"
				columnGap="space.400"
				templateColumns="1fr 1fr"
				xcss={candidatesGridStyle}
			>
				{candidates.map((candidate, index) => (
					<Candidate
						key={index}
						candidate={candidate}
						candidateIndex={index}
						handleCandidateClicked={handleCandidateClicked}
					/>
				))}
			</Grid>
		</Stack>
	);
};

const CandidatesSkeleton = () => {
	return (
		<Stack space="space.200">
			<Skeleton width={100} height={20} />
			<Grid rowGap="space.200" columnGap="space.400" templateColumns="1fr 1fr">
				<Skeleton width={'100%'} height={44} borderRadius={22} />
				<Skeleton width={'100%'} height={44} borderRadius={22} />
				<Skeleton width={'100%'} height={44} borderRadius={22} />
				<Skeleton width={'100%'} height={44} borderRadius={22} />
			</Grid>
		</Stack>
	);
};

const titleMessagesArray = [messages.relatedSearch, messages.teammatesAlsoAsk];

const LeapQuery = ({
	candidates,
	loading,
	titleIndex = 0,
	handleCandidateClicked,
}: LeapQueryProps): React.JSX.Element | null => {
	// The `titleIndex` prop is used to specify which title to display.
	// `titleMessagesArray` is in the order that we aligned with backend engineers who maintains the leap query API.
	return loading ? (
		<Box padding="space.100">
			<CandidatesSkeleton />
		</Box>
	) : candidates.length > 0 ? (
		<Box padding="space.100">
			<Candidates
				candidates={candidates}
				titleIndex={titleIndex}
				handleCandidateClicked={handleCandidateClicked}
			/>
		</Box>
	) : null;
};

export default LeapQuery;
